import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { StyledNavigationBarButton, StyledNavigationBarAnchor, StyledNavigationBar, StyledNavigationBarContainer, StyledNavigationBarItemInner, StyledIconButton, StyledListItem } from './index.styles.js';
import { Icon } from '../icon/index.js';
import { useShowScrollButtons } from '../../hooks/use-show-scroll-buttons.js';
import {} from '@oneaudi/unified-web-common';
import { pickA11yProps } from '../../utils/pick-a11y-props.js';
import { pickDataProps } from '../../utils/pick-data-props.js';
import { useArrowKeyTabs } from '../../hooks/use-arrow-navigation.js';
const NavigationBarItem = forwardRef(({ item, showScrollButtons, size, containerRef }, ref) => {
    const internalRef = useRef(null);
    const iconSize = size === 'md' ? 's' : 'xs';
    const tabItemIcon = item.icon && React.createElement(Icon, { name: item.icon, size: iconSize });
    useImperativeHandle(ref, () => internalRef.current);
    const handleScrollInView = () => {
        const itemEl = internalRef.current;
        const parent = containerRef.current;
        if (!parent || !itemEl) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        const itemRect = itemEl.getBoundingClientRect();
        const isInView = itemRect.left >= parentRect.left && itemRect.right <= parentRect.right;
        if (!isInView) {
            return itemEl.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'start'
            });
        }
    };
    useEffect(() => {
        if (item.active)
            handleScrollInView();
    }, [item.active]);
    useEffect(() => {
        const container = containerRef.current;
        if (item.active && container?.contains(document.activeElement)) {
            internalRef.current?.focus();
        }
    }, [item.active, containerRef]);
    return (React.createElement(StyledListItem, { role: "none" }, item.href ? (React.createElement(StyledNavigationBarAnchor, { active: item.active, "aria-current": item.active ? 'page' : undefined, size: size, role: "menuitem", showScrollButtons: showScrollButtons, href: item.href, onClick: item.onClick, target: item.target, ref: internalRef, rel: item.rel, "aria-label": item['aria-label'], tabIndex: item.active ? 0 : -1 },
        React.createElement(StyledNavigationBarItemInner, { size: size, active: item.active },
            item.iconPosition === 'leading' && tabItemIcon,
            item.label,
            item.iconPosition === 'trailing' && tabItemIcon))) : (React.createElement(StyledNavigationBarButton, { active: item.active, "aria-current": item.active ? 'page' : undefined, size: size, role: "menuitem", onClick: item.onClick, disabled: item.disabled, showScrollButtons: showScrollButtons, ref: internalRef, "aria-label": item['aria-label'], tabIndex: item.active ? 0 : -1 },
        React.createElement(StyledNavigationBarItemInner, { size: size, active: item.active },
            item.iconPosition === 'leading' && tabItemIcon,
            item.label,
            item.iconPosition === 'trailing' && tabItemIcon)))));
});
NavigationBarItem.displayName = 'NavigationBarItem';
export const NavigationBar = React.forwardRef(({ items, size, id, defaultScrollable, scrollButtons, onListElementKeyDown, onKeyNavigate, className, margin, ...rest }, ref) => {
    const a11yProps = pickA11yProps(rest);
    const dataProps = pickDataProps(rest);
    const navigationBarRef = useRef(null);
    const activeIndex = items.findIndex((item) => item.active);
    const { isScrollable, inactiveArrow } = useShowScrollButtons(navigationBarRef);
    const showScrollButtons = defaultScrollable || isScrollable;
    const handleScroll = useCallback((direction, event) => {
        const element = navigationBarRef.current;
        if (!element) {
            return;
        }
        const multiplier = 0.75;
        const scrollLength = element.offsetWidth * multiplier;
        element.scrollBy({
            left: direction === 'left' ? -scrollLength : scrollLength,
            behavior: 'smooth'
        });
        scrollButtons[direction]?.onClick?.(event);
    }, []);
    useArrowKeyTabs(navigationBarRef, items, activeIndex, onKeyNavigate);
    return (React.createElement(StyledNavigationBarContainer, { ...a11yProps, ...dataProps, className: className, ref: ref, margin: margin },
        showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "back", size: "md", "aria-label": scrollButtons.left.ariaLabel, onClick: (e) => handleScroll('left', e), disabled: inactiveArrow === 'left' })),
        React.createElement(StyledNavigationBar, { role: "menu", ref: navigationBarRef, onKeyDown: onListElementKeyDown }, items.map((item, i) => {
            return (React.createElement(NavigationBarItem, { key: `${id}-${i}`, containerRef: navigationBarRef, size: size, item: item, showScrollButtons: showScrollButtons, ref: item.ref }));
        })),
        showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "forward", size: "md", "aria-label": scrollButtons.right.ariaLabel, onClick: (e) => handleScroll('right', e), disabled: inactiveArrow === 'right' }))));
});
NavigationBar.displayName = 'NavigationBar';
//# sourceMappingURL=index.js.map